import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowsChevronBottomIcon,
  ButtonLink,
  Dropdown,
  NotificationsInfoIcon,
  Text,
  DropdownClickEventType,
} from '@wildberries/ui-kit';
import i18next from 'i18next';
import classnames from 'classnames/bind';
import { ALL_INSTRUCTIONS_LIST } from '@/pages/supplies-management/_layout/main-layout/_constants';
import { TAdditionalInstruction } from '@/pages/supplies-management/_layout/main-layout/types';
import { COMMON_TRANSLATIONS } from '@/pages/supplies-management/_constants/translations';
import { InstructionItem } from '@/pages/supplies-management/_layout/main-layout/_components/instruction-item';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'Instructions';

type TProps = {
  additionalInstruction?: TAdditionalInstruction;
  isMobile?: boolean;
  onSendAnalytics?: (title: string) => void;
};

export const Instructions = memo(
  ({
    additionalInstruction,
    isMobile,
    onSendAnalytics = () => {
      ('');
    },
  }: TProps) => {
    const [isOpenUp, setIsOpenUp] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>();

    const handleClickOutside = ({ target }: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target as Node)
      ) {
        setIsOpenUp(false);
      }
    };

    useEffect(() => {
      document.addEventListener('click', handleClickOutside, true);

      return () => {
        document.removeEventListener('click', handleClickOutside, true);
      };
    }, []);

    const currentInstruction = useMemo(
      () =>
        additionalInstruction &&
        ALL_INSTRUCTIONS_LIST.find(
          (instruction) => instruction.value === additionalInstruction,
        ),
      [additionalInstruction],
    );

    const handleDropdownClick = useCallback(
      () => setIsOpenUp(!isOpenUp),
      [isOpenUp],
    );

    const handleSendAnalyticsOptions = useCallback(
      ({ value }: DropdownClickEventType) => {
        onSendAnalytics(value.title);
      },
      [onSendAnalytics],
    );

    const handleSendAnalyticsButton = () => {
      onSendAnalytics(currentInstruction.title);
    };

    return (
      <div className={cn(BLOCK_NAME)}>
        {!isMobile && additionalInstruction && (
          <div className={cn(`${BLOCK_NAME}__inner`)}>
            <NotificationsInfoIcon />
            <ButtonLink
              onClick={handleSendAnalyticsButton}
              routeName={currentInstruction.link}
              target="_blank"
              text={currentInstruction.title}
              textColor="blue"
              variant="list-item"
            />
          </div>
        )}
        <div
          ref={dropdownRef}
          className={cn(`${BLOCK_NAME}__dropdown-container`)}
          role="presentation"
        >
          <div
            className={cn(`${BLOCK_NAME}__dropdown-title`)}
            onClick={handleDropdownClick}
            role="presentation"
          >
            <Text
              color="blue"
              text={i18next.t(COMMON_TRANSLATIONS.allInstructions)}
            />
            <div
              className={cn(`${BLOCK_NAME}__dropdown-icon`, {
                [`${BLOCK_NAME}__dropdown-icon--open`]: isOpenUp,
              })}
            >
              <ArrowsChevronBottomIcon size="S" />
            </div>
          </div>
          {isOpenUp && (
            <div className={cn(`${BLOCK_NAME}__dropdown`)}>
              <Dropdown
                customOptionComponent={InstructionItem}
                items={ALL_INSTRUCTIONS_LIST}
                onChange={handleSendAnalyticsOptions}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);
